import React from 'react';
import { CreditCard, Wine, Pizza, Coffee, MapPin, Film, Car, Camera, Popcorn, Sparkles, Users, Dice5, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import GlobalNav from '../../components/GlobalNav';
import MetaTags from '../../meta/MetaTags.jsx';
import './styles.css';
import './entry-strip.css';
import { encodeRoomId } from '../../utils/roomUtils';
import { api as firestoreApi } from '../../utils/firestoreApi';

const AWARD_PRESETS = [
  { icon: <CreditCard size={20} />, label: 'Pays the bill' },
  { icon: <Wine size={20} />, label: 'Pays for drinks' },
  { icon: <Pizza size={20} />, label: 'Pays for pizza' },
  { icon: <Coffee size={20} />, label: 'Buys coffee' },
  { icon: <MapPin size={20} />, label: 'Picks restaurant' },
  { icon: <Film size={20} />, label: 'Books movie tickets' },
  { icon: <Car size={20} />, label: 'Is the designated driver' },
  { icon: <Camera size={20} />, label: 'Takes group photo' },
  { icon: <Popcorn size={20} />, label: 'Runs snack run' },
  { icon: <Sparkles size={20} />, label: 'Clears the table' },
  { icon: <Dice5 size={20} />, label: 'Chooses activity' },
  { icon: <Users size={20} />, label: 'Hosts next time' },
];

const HERO_ROTATOR_ITEMS = [
  { icon: <CreditCard size={18} />, label: 'Pays the bill' },
  { icon: <Wine size={18} />, label: 'Pays for drinks' },
  { icon: <Coffee size={18} />, label: 'Buys coffee' },
  { icon: <Film size={18} />, label: 'Books movie tickets' },
  { icon: <Car size={18} />, label: 'Is the designated driver' },
  { icon: <Camera size={18} />, label: 'Takes group photo' },
  { icon: <Popcorn size={18} />, label: 'Runs snack run' },
  { icon: <Sparkles size={18} />, label: 'Takes the dare' },
  { icon: <Sparkles size={18} />, label: 'Clears the table' },
  { icon: <Users size={18} />, label: 'Hosts next time' },
];

export default function WhoIsPayingEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSmoke = (import.meta?.env?.VITE_TEST_SMOKE === 'true');
  const [hostName, setHostName] = React.useState('');
  const [award, setAward] = React.useState('');
  const [roomCode, setRoomCode] = React.useState('');
  const [joinName, setJoinName] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [activeForm, setActiveForm] = React.useState('');
  const joinNameRef = React.useRef(null);
  const [heroRotatorIndex, setHeroRotatorIndex] = React.useState(0);

  const ROOM_ID_LENGTH = 6;

  // Prefill room code if present in navigation state or URL
  React.useEffect(() => {
    let code = '';
    let autoJoin = false;
    if (location.state?.roomId) code = String(location.state.roomId);
    else if (location.search) {
      const params = new URLSearchParams(location.search);
      if (params.has('roomCode')) code = params.get('roomCode');
      else if (params.has('code')) code = params.get('code');
      if (params.get('autoJoin') === 'true') autoJoin = true;
    }
    if (code && code.length === ROOM_ID_LENGTH && (autoJoin || location.state?.roomId)) {
      setRoomCode(code);
      setActiveForm('join');
      setTimeout(() => {
        if (joinNameRef.current) joinNameRef.current.focus();
      }, 100);
    }
  }, [location.state, location.search]);

  React.useEffect(() => {
    if (activeForm === 'join' && joinNameRef.current) {
      joinNameRef.current.focus();
    }
  }, [activeForm]);

  React.useEffect(() => {
    if (!HERO_ROTATOR_ITEMS.length) return undefined;
    const intervalId = setInterval(() => {
      setHeroRotatorIndex((prev) => (prev + 1) % HERO_ROTATOR_ITEMS.length);
    }, 4000);
    return () => clearInterval(intervalId);
  }, []);

  const formatName = (v) => v.replace(/[^a-zA-Z ]/g, '').trim().slice(0, 16);
  const isValidName = (v) => /^[A-Za-z ]+$/.test(v) && v.trim().length >= 3;
  const formatAward = (v) => v.trim().slice(0, 25);
  const isValidAward = (v) => v.trim().length >= 4 && v.trim().length <= 25;
  const formatRoomCode = (v) => v.replace(/[^0-9]/g, '').slice(0, ROOM_ID_LENGTH);

  const handleAwardPresetClick = (label) => {
    setAward(label);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    const cleanName = formatName(hostName);
    const cleanAward = formatAward(award);
    if (!isValidName(cleanName)) return setError('Name must be 3-16 letters or spaces.');
    if (!isValidAward(cleanAward)) return setError('Please enter winner text 4-25 characters.');
    setLoading(true);

    try {
      // In smoke test mode, bypass backend and navigate with a fake room
      if (isSmoke) {
        const finalRoomId = '123456';
        const finalHostId = 'host-smoke';
        navigate(`/whoispaying/room/${encodeRoomId(finalRoomId, finalHostId)}`, {
          state: {
            roomId: finalRoomId,
            playerId: finalHostId,
            displayName: cleanName,
            hostName: cleanName,
            hostId: finalHostId,
            award: cleanAward,
            expiresAt: null,
            showWizard: true,
          }
        });
        return;
      }
      const result = await firestoreApi('create', { hostName: cleanName, award: cleanAward });
      const finalRoomId = result.roomId;
      const finalHostId = result.hostId;
      navigate(`/whoispaying/room/${encodeRoomId(finalRoomId, finalHostId)}`, {
        state: {
          roomId: finalRoomId,
          playerId: finalHostId,
          displayName: cleanName,
          hostName: cleanName,
          hostId: finalHostId,
          award: cleanAward,
          expiresAt: result.expiresAt || null,
          showWizard: true,
        }
      });
    } catch (err) {
      setError(err?.message || 'Failed to create room.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setError('');
    const code = formatRoomCode(roomCode);
    const cleanName = formatName(joinName);
    if (code.length !== ROOM_ID_LENGTH) return setError('Room code must be six digits.');
    if (!isValidName(cleanName)) return setError('Name must be 3-16 letters or spaces.');
    setLoading(true);
    try {
      const result = await firestoreApi('join', { roomId: code, name: cleanName });
      navigate(`/whoispaying/room/${encodeRoomId(result.roomId, result.hostId)}`, {
        state: { roomId: result.roomId, playerId: result.playerId, displayName: cleanName, award: result.award, hostId: result.hostId ?? null, hostName: result.hostName || null, expiresAt: result.expiresAt ?? null }
      });
    } catch (err) {
      if (err?.message?.includes('Authentication failed')) {
        setError('Could not authenticate. Please refresh the page and try again.');
      } else {
        setError(err?.message || 'Failed to join room.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
  <>
    <MetaTags profileKey="whoIsPaying" />
    <GlobalNav />
      <div className="section-entry-wrapper" data-testid="whoispaying-entry" aria-live="polite">
      {loading && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <div className="loading-card">
            <div className="loading-spinner" aria-hidden="true"></div>
            <div className="loading-text">
              <div className="loading-line">Getting your room ready…</div>
              <div className="loading-sub">
                {activeForm === 'create'
                  ? 'Rolling the dice for a new room'
                  : activeForm === 'join'
                    ? 'Shuffling cards to join the table'
                    : 'Shuffling things into place'}
                {' '}
                🎲
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="entry-hero">
        <h1 className="entry-hero-title">Decide Together, the Fun Way!</h1>
        <div className="hero-tagline-container">
          <p className="hero-tagline-text">Decide who</p>
          <div className="hero-rotator-badge-wrapper">
            <span
              key={heroRotatorIndex}
              className="hero-rotator-badge"
              aria-live="polite"
              aria-label="Who is paying suggestions"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {HERO_ROTATOR_ITEMS[heroRotatorIndex].icon}
              <span>{HERO_ROTATOR_ITEMS[heroRotatorIndex].label}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="game-card-compact game-card-compact--inline">
        <div className="game-icons-row-compact">
          <span className="game-icon-compact" aria-hidden="true"><Dice5 size={20} /></span>
          <span className="game-icon-divider">or</span>
          <span className="game-icon-compact" aria-hidden="true"><Users size={20} /></span>
        </div>
        <h3>Who Is Paying?</h3>
        <p>Settle it with dice or cards</p>
      </div>

      <div
        className="entry-steps-strip"
        aria-label="How it works in three steps"
      >
        <div className="entry-steps-strip-item">
          <div className="entry-steps-strip-icon"><Sparkles size={18} /></div>
          <div className="entry-steps-strip-body">
            <div className="entry-steps-strip-title">Create</div>
            <div className="entry-steps-strip-text">
              Name your room
            </div>
          </div>
        </div>
        
  <div className="entry-steps-strip-arrow"><ArrowRight size={16} /></div>
        
        <div className="entry-steps-strip-item">
          <div className="entry-steps-strip-icon"><Users size={18} /></div>
          <div className="entry-steps-strip-body">
            <div className="entry-steps-strip-title">Invite</div>
            <div className="entry-steps-strip-text">
              Share the link
            </div>
          </div>
        </div>
        
  <div className="entry-steps-strip-arrow"><ArrowRight size={16} /></div>
        
        <div className="entry-steps-strip-item">
          <div className="entry-steps-strip-icon"><Dice5 size={18} /></div>
          <div className="entry-steps-strip-body">
            <div className="entry-steps-strip-title">Decide</div>
            <div className="entry-steps-strip-text">
              Let luck choose
            </div>
          </div>
        </div>
      </div>

      

      <div className="section-entry">
        <h2 className="section-header">Create Room / Join Room</h2>

        <div className="block-entry-chooser">
          <button 
            className={`block-entry-choice${activeForm === 'create' ? ' is-active' : ''}`} 
            onClick={() => setActiveForm('create')} 
            disabled={loading}
          >
            Create Room
          </button>
          <button 
            className={`block-entry-choice${activeForm === 'join' ? ' is-active' : ''}`} 
            onClick={() => setActiveForm('join')} 
            disabled={loading}
          >
            Join Room
          </button>
        </div>

        <div className={`block-entry-forms${activeForm ? ' block-entry-forms--' + activeForm : ''}`}>
          {activeForm === 'create' && (
            <form className="block-create-form" onSubmit={handleCreate} data-testid="create-form">
              <h3 className="section-subheader">Create</h3>
              
              <label htmlFor="create-display-name" className="input-label">Display Name</label>
              <input
                id="create-display-name"
                className="control-input"
                placeholder="Display Name"
                value={hostName}
                onChange={(e) => setHostName(e.target.value)}
                onBlur={(e) => setHostName(formatName(e.target.value))}
                maxLength={16}
                required
                data-testid="create-name"
              />
              
              <label htmlFor="create-award" className="input-label">Winner (Type or pick below)</label>
              <input
                id="create-award"
                className="control-input"
                placeholder="Winner (e.g. Buys coffee)"
                value={award}
                onChange={(e) => setAward(e.target.value)}
                maxLength={25}
                required
                data-testid="create-award"
              />
              
              <div className="award-presets">
                {AWARD_PRESETS.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="award-preset-chip"
                    onClick={() => handleAwardPresetClick(preset.label)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    {preset.icon}
                    <span className="award-preset-label">{preset.label}</span>
                  </button>
                ))}
              </div>
              
              <button className="control-button" type="submit" disabled={loading} aria-label="Start a Fun Decider" data-testid="create-submit">
                {loading ? 'Creating...' : 'Create Room'}
              </button>
            </form>
          )}
          {activeForm === 'join' && (
            <form className="block-join-form" onSubmit={handleJoin} data-testid="join-form">
              <h3 className="section-subheader">Join</h3>
              <label htmlFor="join-room-code" className="input-label">Room Code</label>
              <input
                id="join-room-code"
                className="control-input"
                placeholder="Room Code"
                value={roomCode}
                onChange={(e) => setRoomCode(formatRoomCode(e.target.value))}
                inputMode="numeric"
                maxLength={ROOM_ID_LENGTH}
                required
                data-testid="join-code"
              />
              <label htmlFor="join-name" className="input-label">Name</label>
              <input
                id="join-name"
                className="control-input"
                placeholder="Your Name"
                value={joinName}
                onChange={(e) => setJoinName(e.target.value)}
                onBlur={(e) => setJoinName(formatName(e.target.value))}
                maxLength={16}
                required
                data-testid="join-name"
                ref={joinNameRef}
              />
              <button className="control-button" type="submit" disabled={loading} aria-label="Join Room" data-testid="join-submit">
                {loading ? 'Joining...' : 'Join'}
              </button>
            </form>
          )}
        </div>

        {error && <div className="block-error" role="alert">{error}</div>}
      </div>
    </div>
  </>
  );
}
